import React, { useState, useEffect } from 'react';
import { useContent } from '../../contexts/ContentContext';
import GalleryManager from './GalleryManager';
import ImageUpload from './ImageUpload';
import TestimonialsAdmin from './TestimonialsAdmin';
import SessionsAdmin from './SessionsAdmin';
import FeaturedProgramsAdmin from './FeaturedProgramsAdmin';
import FeaturesHighlightAdmin from './FeaturesHighlightAdmin';
import ServicesAdmin from './ServicesAdmin';
import ValuesAdmin from './ValuesAdmin';
import TimelineAdmin from './TimelineAdmin';
import ThreeBlocksAdmin from './ThreeBlocksAdmin';
import ExpertsTeamAdmin from './ExpertsTeamAdmin';
import ContactSectionAdmin from './ContactSectionAdmin';
import HeroVideoAdmin from './HeroVideoAdmin';

const ContentEditor = ({ contentPath }) => {
  const { getContent, updateContent } = useContent();
  const [sectionData, setSectionData] = useState(null);
  const [expandedItems, setExpandedItems] = useState({});

  useEffect(() => {
    const data = getContent(contentPath);
    setSectionData(data);
    
    // Auto-expand formation items for better UX
    if (contentPath === 'formations.formationsGrid' && data?.formations) {
      const newExpandedItems = {};
      data.formations.forEach((_, index) => {
        newExpandedItems[`${contentPath}.formations.${index}`] = true;
      });
      setExpandedItems(newExpandedItems);
    }
  }, [contentPath, getContent]);

  const handleInputChange = (path, value) => {
    updateContent(path, value);
    setSectionData(getContent(contentPath));
  };

  const toggleExpanded = (key) => {
    setExpandedItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const isGalleryArray = (path, key) => {
    // Check if we're editing expertGallery photos or videos from about or experts pages
    return (path.includes('expertGallery') && (key === 'photos' || key === 'videos'));
  };

  const isExpertGallery = (path) => {
    return path.includes('expertGallery');
  };

  const isTestimonialsSection = (path) => {
    return path.includes('Testimonials') && (path.includes('expert') || path.includes('participant'));
  };

  const isUpcomingSessionsSection = (path) => {
    return path.includes('upcomingSessions');
  };

  const isFeaturedProgramsSection = (path) => {
    return path.includes('featuredPrograms');
  };

  const isFeaturesHighlightSection = (path) => {
    return path.includes('featuresHighlight');
  };

  const isServicesSection = (path) => {
    return path.includes('mainServices');
  };

  const isValuesSection = (path) => {
    return path.includes('coreValues');
  };

  const isTimelineSection = (path) => {
    return path.includes('chronologie');
  };

  const isThreeBlocksSection = (path) => {
    return path.includes('threeBlocks');
  };

  const isExpertsTeamSection = (path) => {
    return path.includes('experts.team');
  };

  const isContactArraySection = (path) => {
    return path.includes('contact.pageHeader') ||
           path.includes('contact.contactInfo') ||
           path.includes('contact.contactForm') || 
           path.includes('contact.quickActions') || 
           path.includes('contact.trainingLocations') ||
           path.includes('contact.registrationLinks') ||
           path.includes('contact.faq') ||
           path.includes('contact.socialNetworks') ||
           path.includes('contact.cta');
  };

  const isImageField = (key) => {
    // Check if this is an image field that should use file upload
    const imageFields = [
      'formationPhoto', 'instructorPhoto', 'photo', 'image', 'picture',
      'avatar', 'banner', 'thumbnail', 'logo', 'icon'
    ];
    const lowerKey = key.toLowerCase();
    return imageFields.some(field => lowerKey.includes(field));
  };

  const isVideoField = (key) => {
    return key === 'videoUrl' || key.toLowerCase().includes('video');
  };

  const isHeroSection = (path) => {
    return path === 'home.hero';
  };

  const getGalleryType = (key) => {
    if (key === 'photos') return 'photos';
    if (key === 'videos') return 'videos';
    return 'mixed';
  };

  // Tabbed Gallery Component
  const TabbedGallery = ({ fullPath, galleryData, onUpdate }) => {
    const [activeTab, setActiveTab] = useState('photos');
    
    if (!galleryData || typeof galleryData !== 'object') return null;
    
    const photos = galleryData.photos || [];
    const videos = galleryData.videos || [];
    const tabs = galleryData.tabs || [
      { id: 'photos', icon: 'fas fa-images', text: 'Photos' },
      { id: 'videos', icon: 'fas fa-video', text: 'Vidéos' }
    ];
    
    return (
      <div className="tabbed-gallery">
        {/* Tab Navigation */}
        <div className="gallery-tabs" style={{
          display: 'flex',
          borderBottom: '2px solid #e5e7eb',
          marginBottom: '20px'
        }}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '12px 20px',
                border: 'none',
                background: activeTab === tab.id ? '#3b82f6' : 'transparent',
                color: activeTab === tab.id ? 'white' : '#6b7280',
                cursor: 'pointer',
                borderRadius: '6px 6px 0 0',
                fontWeight: activeTab === tab.id ? '600' : '400',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s'
              }}
            >
              <i className={tab.icon}></i>
              {tab.text}
            </button>
          ))}
        </div>
        
        {/* Tab Content */}
        <div className="gallery-tab-content">
          {activeTab === 'photos' && (
            <div>
              <GalleryManager
                value={photos}
                onChange={(newPhotos) => onUpdate(`${fullPath}.photos`, newPhotos)}
                type="photos"
              />
            </div>
          )}
          
          {activeTab === 'videos' && (
            <div>
              <GalleryManager
                value={videos}
                onChange={(newVideos) => onUpdate(`${fullPath}.videos`, newVideos)}
                type="videos"
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderTextInput = (fullPath, value, label, type = 'text') => (
    <div className="form-group">
      <label className="form-label">
        <i className="fas fa-text-width"></i>
        {label}
      </label>
      {type === 'textarea' ? (
        <textarea
          className="form-input textarea"
          value={value || ''}
          onChange={(e) => handleInputChange(fullPath, e.target.value)}
          rows={3}
        />
      ) : (
        <input
          type={type}
          className="form-input"
          value={value || ''}
          onChange={(e) => handleInputChange(fullPath, e.target.value)}
        />
      )}
    </div>
  );

  const renderArrayEditor = (fullPath, array, itemTemplate) => {
    if (!Array.isArray(array)) return null;

    return (
      <div className="array-editor">
        {array.map((item, index) => {
          const isStringItem = typeof item === 'string';
          let itemPreview;
          let arrayItemType = 'default';
          
          if (isStringItem) {
            itemPreview = item.length > 50 ? item.substring(0, 50) + '...' : item;
          } else if (typeof item === 'object' && item !== null) {
            // Create meaningful previews for different object types
            if (item.name && item.rating) {
              itemPreview = `Avis de ${item.name} (${item.rating}⭐)`;
              arrayItemType = 'reviews';
            } else if (item.date && item.location) {
              itemPreview = `Session ${item.date} - ${item.location}`;
              arrayItemType = 'sessions';
            } else if (item.module && item.title) {
              itemPreview = `${item.module}: ${item.title}`;
              arrayItemType = 'program';
            } else if (item.title && item.formationPhoto) {
              itemPreview = `Formation: ${item.title}`;
              arrayItemType = 'formations';
            } else if (item.title) {
              itemPreview = item.title;
            } else {
              itemPreview = `Item ${index + 1}`;
            }
          } else {
            itemPreview = `Item ${index + 1}`;
          }
          
          return (
            <div key={index} className="array-item" data-type={arrayItemType}>
              <div className="array-item-header">
                <h4>
                  <i className="fas fa-list-ol"></i>
                  {itemPreview}
                </h4>
                {!isStringItem && (
                  <button 
                    className="btn-toggle"
                    onClick={() => toggleExpanded(`${fullPath}.${index}`)}
                    title={expandedItems[`${fullPath}.${index}`] ? 'Réduire' : 'Développer'}
                  >
                    <i className={`fas fa-chevron-${expandedItems[`${fullPath}.${index}`] ? 'up' : 'down'}`}></i>
                    <span className="toggle-text">
                      {expandedItems[`${fullPath}.${index}`] ? 'Réduire' : 'Développer'}
                    </span>
                  </button>
                )}
                <button 
                  className="btn-remove"
                  onClick={() => {
                    const newArray = array.filter((_, i) => i !== index);
                    handleInputChange(fullPath, newArray);
                  }}
                  title="Supprimer l'élément"
                >
                  <i className="fas fa-trash"></i>
                </button>
              </div>
              
              {isStringItem ? (
                <div className="array-item-content">
                  <input
                    type="text"
                    className="form-input"
                    value={item}
                    onChange={(e) => {
                      const newArray = [...array];
                      newArray[index] = e.target.value;
                      handleInputChange(fullPath, newArray);
                    }}
                    placeholder="Saisir le texte..."
                  />
                </div>
              ) : (
                expandedItems[`${fullPath}.${index}`] !== false && (
                  <div className="array-item-content">
                    {renderObjectEditor(`${fullPath}.${index}`, item)}
                  </div>
                )
              )}
            </div>
          );
        })}
        <button 
          className="btn-add-item"
          onClick={() => {
            const newArray = [...array, itemTemplate];
            handleInputChange(fullPath, newArray);
          }}
        >
          <i className="fas fa-plus"></i>
          Ajouter un élément
        </button>
      </div>
    );
  };

  const renderObjectEditor = (fullPath, obj) => {
    if (!obj || typeof obj !== 'object') return null;

    // Special handling for expertGallery - render ONLY the tabbed gallery interface
    if (isExpertGallery(fullPath)) {
      return (
        <div className="object-editor">
          {/* Only show the tabbed gallery - no individual field editors */}
          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-photo-video"></i>
              Galerie Photos & Vidéos
            </label>
            <TabbedGallery 
              fullPath={fullPath} 
              galleryData={obj} 
              onUpdate={handleInputChange} 
            />
          </div>
        </div>
      );
    }

    return (
      <div className="object-editor">
        {Object.entries(obj).map(([key, value]) => {
          const currentPath = `${fullPath}.${key}`;
          
          if (typeof value === 'string') {
            // Check if this is the hero video field
            if (isVideoField(key) && isHeroSection(fullPath)) {
              return (
                <div key={key} className="form-group">
                  <HeroVideoAdmin
                    videoUrl={value}
                    onChange={(newValue) => handleInputChange(currentPath, newValue)}
                  />
                </div>
              );
            }
            
            // Check if this is an image field
            if (isImageField(key)) {
              return (
                <div key={key} className="form-group">
                  <label className="form-label">
                    <i className="fas fa-image"></i>
                    {key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}
                  </label>
                  <ImageUpload
                    value={value}
                    onChange={(newValue) => handleInputChange(currentPath, newValue)}
                    label={key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}
                    placeholder={`Télécharger ${key.toLowerCase().includes('instructor') ? 'photo du formateur' : 'photo de formation'}`}
                  />
                </div>
              );
            }
            
            const isLongText = value.length > 100 || value.includes('\n');
            return renderTextInput(
              currentPath, 
              value, 
              key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1'),
              isLongText ? 'textarea' : 'text'
            );
          }
          
          if (typeof value === 'number') {
            return renderTextInput(
              currentPath, 
              value, 
              key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1'),
              'number'
            );
          }
          
          if (Array.isArray(value)) {
            // Create appropriate template based on array content type and field name
            let template;
            if (value.length > 0) {
              const firstItem = value[0];
              if (typeof firstItem === 'string') {
                template = '';
              } else if (typeof firstItem === 'number') {
                template = 0;
              } else if (typeof firstItem === 'object') {
                template = JSON.parse(JSON.stringify(firstItem));
              } else {
                template = firstItem;
              }
            } else {
              // Create default templates for specific formation fields
              if (key === 'sessions') {
                template = {
                  date: '',
                  location: '',
                  spots: ''
                };
              } else if (key === 'reviews') {
                template = {
                  name: '',
                  rating: 5,
                  comment: '',
                  date: ''
                };
              } else if (key === 'program') {
                template = {
                  module: '',
                  title: '',
                  duration: '',
                  content: []
                };
              } else if (key === 'objectives' || key === 'level') {
                template = '';
              } else if (key === 'formations') {
                template = {
                  id: '',
                  title: '',
                  subtitle: '',
                  icon: '',
                  instructor: '',
                  level: [],
                  description: '',
                  color: '',
                  formationPhoto: '',
                  instructorPhoto: '',
                  objectives: [],
                  program: [],
                  reviews: [],
                  sessions: []
                };
              } else {
                template = '';
              }
            }
            
            // Check if this is a gallery array (photos or videos)
            if (isGalleryArray(currentPath, key)) {
              return (
                <div key={key} className="form-group">
                  <label className="form-label">
                    <i className={`fas ${key === 'photos' ? 'fa-images' : 'fa-video'}`}></i>
                    {key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}
                  </label>
                  <GalleryManager
                    value={value}
                    onChange={(newValue) => handleInputChange(currentPath, newValue)}
                    type={getGalleryType(key)}
                  />
                </div>
              );
            }

            return (
              <div key={key} className="form-group">
                <label className="form-label">
                  <i className="fas fa-list"></i>
                  {key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}
                </label>
                {renderArrayEditor(currentPath, value, template)}
              </div>
            );
          }
          
          if (typeof value === 'object' && value !== null) {
            return (
              <div key={key} className="form-group">
                <label className="form-label">
                  <i className="fas fa-folder"></i>
                  {key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}
                </label>
                <div className="nested-object">
                  {renderObjectEditor(currentPath, value)}
                </div>
              </div>
            );
          }
          
          return null;
        })}
      </div>
    );
  };

  if (sectionData === null || sectionData === undefined) {
    return (
      <div className="editor-loading">
        <i className="fas fa-exclamation-triangle"></i>
        <span>Contenu non trouvé pour: {contentPath}</span>
        <div style={{ marginTop: '10px', fontSize: '0.9rem', color: '#666' }}>
          <p>Chemin: {contentPath}</p>
          <button 
            onClick={() => window.location.reload()} 
            style={{ padding: '8px 16px', background: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
          >
            Recharger la page
          </button>
        </div>
      </div>
    );
  }

  // Use specialized TestimonialsAdmin for testimonial sections
  if (isTestimonialsSection(contentPath)) {
    return <TestimonialsAdmin contentPath={contentPath} />;
  }

  // Use specialized SessionsAdmin for upcoming sessions
  if (isUpcomingSessionsSection(contentPath)) {
    return <SessionsAdmin contentPath={contentPath} />;
  }

  // Use specialized FeaturedProgramsAdmin for featured programs
  if (isFeaturedProgramsSection(contentPath)) {
    return <FeaturedProgramsAdmin contentPath={contentPath} />;
  }

  // Use specialized FeaturesHighlightAdmin for features highlight
  if (isFeaturesHighlightSection(contentPath)) {
    return <FeaturesHighlightAdmin contentPath={contentPath} />;
  }

  // Use specialized ServicesAdmin for main services
  if (isServicesSection(contentPath)) {
    return <ServicesAdmin contentPath={contentPath} />;
  }

  // Use specialized ValuesAdmin for core values
  if (isValuesSection(contentPath)) {
    return <ValuesAdmin contentPath={contentPath} />;
  }

  // Use specialized TimelineAdmin for chronologie
  if (isTimelineSection(contentPath)) {
    return <TimelineAdmin contentPath={contentPath} />;
  }

  // Use specialized ThreeBlocksAdmin for three blocks
  if (isThreeBlocksSection(contentPath)) {
    return <ThreeBlocksAdmin contentPath={contentPath} />;
  }

  // Use specialized ExpertsTeamAdmin for experts team
  if (isExpertsTeamSection(contentPath)) {
    return <ExpertsTeamAdmin contentPath={contentPath} />;
  }

  // Use specialized ContactSectionAdmin for contact sections with arrays
  if (isContactArraySection(contentPath)) {
    return <ContactSectionAdmin contentPath={contentPath} />;
  }

  return (
    <div className="content-editor">
      <div className="editor-toolbar">
        <button className="btn-secondary" onClick={() => window.location.reload()}>
          <i className="fas fa-undo"></i>
          Réinitialiser les modifications
        </button>
        <button className="btn-info" onClick={() => console.log('Current data:', sectionData)}>
          <i className="fas fa-code"></i>
          Voir JSON
        </button>
      </div>
      
      <div className="editor-form">
        {renderObjectEditor(contentPath, sectionData)}
      </div>
    </div>
  );
};

export default ContentEditor;
